import axios, { AxiosError } from "axios";
import { z } from "zod";

export const apiClient = axios.create({
  baseURL: "/api/v1",
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

const sessionTierSchema = z.enum(["free", "paid"]);

export const medicalRecordStatusSchema = z.enum(["pending", "uploaded", "extracted", "opted_out"]);

export const plannedSectionSchema = z.object({
  section_type: z.string(),
  variant: z.string(),
  title: z.string(),
});

export const letterPlanSchema = z.object({
  mode: z.enum(["full", "no_record", "blocked"]),
  sections: z.array(plannedSectionSchema),
});

export const createSessionResponseSchema = z.object({
  session_id: z.string(),
  expires_at: z.string(),
  tier: sessionTierSchema,
  ws_url: z.string(),
});

export const getSessionResponseSchema = z.object({
  session_id: z.string(),
  tier: sessionTierSchema,
  has_ocr: z.boolean(),
  expires_at: z.string(),
  message_count: z.number(),
  // Bypass-mode flag — true when the backend has no STRIPE_SECRET_KEY
  // and tier flips happen via /payments/bypass-confirm rather than a
  // real webhook (docs/plans/13 §M5).
  bypass_mode: z.boolean().optional().default(false),
  // True once the session has a payment_email on file (either set by
  // the Stripe Checkout webhook, or captured by the post-export
  // feedback card). The card uses this to decide whether to render
  // the email input or skip straight to rating/comment.
  has_payment_email: z.boolean().optional().default(false),
  stripe_customer_id: z.string().nullable().optional(),
  stripe_checkout_session_id: z.string().nullable().optional(),
  // The medical-record decision + the deterministic letter plan it
  // produces. Optional-with-default so the SPA degrades gracefully
  // against a backend that predates these fields.
  medical_record_status: medicalRecordStatusSchema.optional().default("pending"),
  letter_plan: letterPlanSchema.nullable().optional().default(null),
  weak_policy: z.boolean().optional().default(false),
  citations_verified: z.boolean().optional().default(false),
  member_statement: z.string().nullable().optional().default(null),
});

export const medicalRecordOptOutResponseSchema = z.object({
  medical_record_status: medicalRecordStatusSchema,
  letter_plan: letterPlanSchema,
});

const yesNoUnsure = z.enum(["yes", "no", "not_sure"]);
const recordKind = z.enum([
  "h_and_p",
  "discharge_summary",
  "consult_notes",
  "labs",
  "imaging",
  "none",
]);

export const memberStatementAnswersSchema = z.object({
  clinician_recommended: yesNoUnsure,
  clinician_note: z.string().max(280).default(""),
  impact_note: z.string().max(280).default(""),
  tried_first: yesNoUnsure,
  tried_note: z.string().max(280).default(""),
  records_available: z.array(recordKind).default([]),
  additional_note: z.string().max(280).default(""),
});

export const memberStatementResponseSchema = z.object({
  ok: z.boolean(),
  member_statement: z.string(),
});

export const checkoutResponseSchema = z.object({
  checkout_url: z.string(),
  bypass: z.boolean().optional(),
});

export const deliveryEmailResponseSchema = z.object({
  saved: z.boolean(),
  has_payment_email: z.boolean(),
});

export const recoverSessionResponseSchema = z.object({
  appeal_session_id: z.string(),
  tier: z.literal("paid"),
  expires_at: z.string(),
  recovery_access_token: z.string(),
  expires_in: z.number(),
  stripe_customer_id: z.string().nullable().optional(),
  stripe_checkout_session_id: z.string().nullable().optional(),
});

export const downloadsResponseSchema = z.object({
  pdf_download_url: z.string().nullable().optional(),
  docx_download_url: z.string().nullable().optional(),
  available_until: z.string(),
  link_expires_in: z.number(),
});

export const deliveryRecoverResponseSchema = z.object({
  appeal_session_id: z.string(),
  tier: z.literal("paid"),
  // "downloads" → artifacts exist, deep-link to the download card.
  // "resume"    → not finished yet, drop into the paid workspace.
  // Optional w/ default for graceful degradation if the backend
  // predates this field.
  mode: z.enum(["downloads", "resume"]).optional().default("downloads"),
  recovery_access_token: z.string(),
  expires_in: z.number(),
  expires_at: z.string(),
  available_until: z.string(),
  delivery_id: z.string(),
  stripe_checkout_session_id: z.string().nullable().optional(),
  stripe_customer_id: z.string().nullable().optional(),
});

export const deliveryResendResponseSchema = z.object({
  status: z.literal("queued"),
  resend_count: z.number(),
  lifetime_remaining: z.number(),
});

export const legalDocumentResponseSchema = z.object({
  kind: z.string(),
  version: z.string(),
  title: z.string(),
  effective_date: z.string(),
  body: z.string(),
});

export const consentDocumentRefSchema = z.object({
  kind: z.string(),
  version: z.string(),
  title: z.string(),
});

export const consentBundleResponseSchema = z.object({
  bundle_version: z.string(),
  bundle_hash: z.string(),
  consent_copy: z.string(),
  documents: z.array(consentDocumentRefSchema),
});

export const pricingResponseSchema = z.object({
  paid: z.object({
    original_price_cents: z.number(),
    offer_price_cents: z.number().nullable(),
    effective_price_cents: z.number(),
    offer_label: z.string().nullable(),
    has_offer: z.boolean(),
  }),
});

export type SessionTier = z.infer<typeof sessionTierSchema>;
export type MedicalRecordStatus = z.infer<typeof medicalRecordStatusSchema>;
export type LetterPlanDto = z.infer<typeof letterPlanSchema>;
export type CreateSessionResponse = z.infer<typeof createSessionResponseSchema>;
export type GetSessionResponse = z.infer<typeof getSessionResponseSchema>;
export type MedicalRecordOptOutResponse = z.infer<typeof medicalRecordOptOutResponseSchema>;
export type MemberStatementAnswers = z.infer<typeof memberStatementAnswersSchema>;
export type MemberStatementResponse = z.infer<typeof memberStatementResponseSchema>;
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;
export type DeliveryEmailResponse = z.infer<typeof deliveryEmailResponseSchema>;
export type RecoverSessionResponse = z.infer<typeof recoverSessionResponseSchema>;
export type DownloadsResponse = z.infer<typeof downloadsResponseSchema>;
export type DeliveryRecoverResponse = z.infer<typeof deliveryRecoverResponseSchema>;
export type DeliveryResendResponse = z.infer<typeof deliveryResendResponseSchema>;
export type PricingResponse = z.infer<typeof pricingResponseSchema>;
export type LegalDocumentResponse = z.infer<typeof legalDocumentResponseSchema>;
export type ConsentBundleResponse = z.infer<typeof consentBundleResponseSchema>;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function wrap(err: unknown): ApiError {
  if (err instanceof AxiosError) {
    const detail = err.response?.data;
    const message =
      (typeof detail === "object" &&
        detail !== null &&
        "detail" in detail &&
        typeof (detail as { detail: unknown }).detail === "string" &&
        (detail as { detail: string }).detail) ||
      err.message;
    return new ApiError(message, err.response?.status, detail);
  }
  return new ApiError(err instanceof Error ? err.message : "Unknown error");
}

export async function createSession(): Promise<CreateSessionResponse> {
  try {
    const { data } = await apiClient.post("/sessions");
    return createSessionResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function getSession(sessionId: string): Promise<GetSessionResponse> {
  try {
    const { data } = await apiClient.get(`/sessions/${sessionId}`);
    return getSessionResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

/** Version of the pre-payment records-readiness warning the user
 *  acknowledged before choosing to proceed without a medical record.
 *  Sent to the opt-out endpoint so we can always show what they saw. */
export const RECORDS_READINESS_ACK = "records-readiness-v1";

/**
 * Deterministic, consent-capturing choice to proceed WITHOUT a medical
 * record → the coverage-policy letter. Paid-only; the backend rejects it
 * after export or once a record has been provided.
 */
export async function optOutMedicalRecord(
  sessionId: string,
  ackVersion: string = RECORDS_READINESS_ACK,
): Promise<MedicalRecordOptOutResponse> {
  try {
    const { data } = await apiClient.post(`/sessions/${sessionId}/medical-record/opt-out`, {
      ack_version: ackVersion,
    });
    return medicalRecordOptOutResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

/** Version of the member-statement intake attestation the member confirms
 *  ("this is my own account, accurate to the best of my knowledge"). */
export const MEMBER_STATEMENT_ACK = "member-statement-v1";

/**
 * Copy-edit the member's free-text notes (spelling/grammar only) and render
 * the attributed statement for review. NOT stored — the member confirms or
 * edits the result via `confirmMemberStatement`.
 */
export async function previewMemberStatement(
  sessionId: string,
  answers: MemberStatementAnswers,
): Promise<MemberStatementResponse> {
  try {
    const { data } = await apiClient.post(`/sessions/${sessionId}/member-statement/preview`, {
      answers,
    });
    return memberStatementResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

/** Store the member-confirmed statement (the exact text they attested to). */
export async function confirmMemberStatement(
  sessionId: string,
  body: string,
  ackVersion: string = MEMBER_STATEMENT_ACK,
): Promise<MemberStatementResponse> {
  try {
    const { data } = await apiClient.post(`/sessions/${sessionId}/member-statement`, {
      ack_version: ackVersion,
      body,
    });
    return memberStatementResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

// Document intake (uploadDocument / uploadDenialText) lives in
// lib/uploadsApi.ts to keep this module under the LOC ceiling.

export async function saveDeliveryEmail(
  sessionId: string,
  email: string,
): Promise<DeliveryEmailResponse> {
  try {
    const { data } = await apiClient.put(`/sessions/${sessionId}/delivery-email`, { email });
    return deliveryEmailResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function startCheckout(
  sessionId: string,
  email?: string,
  recordImportanceAckVersion?: string,
): Promise<CheckoutResponse> {
  try {
    const { data } = await apiClient.post("/payments/checkout", {
      session_id: sessionId,
      tier: "paid",
      ...(email ? { email } : {}),
      ...(recordImportanceAckVersion
        ? { record_importance_ack_version: recordImportanceAckVersion }
        : {}),
    });
    return checkoutResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function recoverSession(
  stripeSessionId: string,
  returnToken: string,
): Promise<RecoverSessionResponse> {
  try {
    const { data } = await apiClient.post("/sessions/recover", {
      stripe_session_id: stripeSessionId,
      return_token: returnToken,
    });
    return recoverSessionResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function getAppealDownloads(
  appealSessionId: string,
  recoveryAccessToken: string,
): Promise<DownloadsResponse> {
  try {
    const { data } = await apiClient.get(`/appeals/${appealSessionId}/downloads`, {
      headers: { Authorization: `Bearer ${recoveryAccessToken}` },
    });
    return downloadsResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function recoverDelivery(token: string): Promise<DeliveryRecoverResponse> {
  try {
    const { data } = await apiClient.post("/appeals/delivery/recover", { token });
    return deliveryRecoverResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function resendDeliveryEmail(
  appealSessionId: string,
  recoveryAccessToken: string,
): Promise<DeliveryResendResponse> {
  try {
    const { data } = await apiClient.post(
      `/appeals/${appealSessionId}/delivery-email/resend`,
      {},
      {
        headers: {
          Authorization: `Bearer ${recoveryAccessToken}`,
        },
      },
    );
    return deliveryResendResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function getPricing(): Promise<PricingResponse> {
  try {
    const { data } = await apiClient.get("/pricing");
    return pricingResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function getConsentBundle(): Promise<ConsentBundleResponse> {
  try {
    const { data } = await apiClient.get("/legal/consent");
    return consentBundleResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

export async function getLegalDocument(kind: string): Promise<LegalDocumentResponse> {
  try {
    const { data } = await apiClient.get(`/legal/documents/${kind}`);
    return legalDocumentResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}

/**
 * Public "Restore my session" request. Always resolves (the backend returns a
 * generic 200 whether or not the email matches), so the UI must show the same
 * neutral confirmation regardless. Network/5xx still throw.
 */
export async function requestDeliveryRecovery(email: string): Promise<void> {
  try {
    await apiClient.post("/appeals/delivery/recover-request", { email });
  } catch (err) {
    throw wrap(err);
  }
}

const bypassConfirmResponseSchema = z.object({
  tier: z.literal("paid"),
  bypass: z.boolean(),
});

export type BypassConfirmResponse = z.infer<typeof bypassConfirmResponseSchema>;

/**
 * Bypass-only tier flip endpoint. Live mode rejects with 400 — the
 * FE only calls this when `getSession` reports `bypass_mode === true`.
 */
export async function confirmBypassUpgrade(sessionId: string): Promise<BypassConfirmResponse> {
  try {
    const { data } = await apiClient.post("/payments/bypass-confirm", {
      session_id: sessionId,
    });
    return bypassConfirmResponseSchema.parse(data);
  } catch (err) {
    throw wrap(err);
  }
}
